import type { MarketingFrame } from "../types";

export interface NavigationSnapshot {
  state: "managed";
  header: "visible" | "hidden";
  routeVisible: boolean;
  footerReached: boolean;
}

export interface MarketingNavigationMount {
  applyNavigationSnapshot(snapshot: NavigationSnapshot): void;
  destroy?(): void;
}

export interface MarketingNavigationOptions {
  hideAfterPx?: number;
  showAfterPx?: number;
  mount?: MarketingNavigationMount;
}

export class MarketingNavigationController {
  private readonly hideAfterPx: number;
  private readonly showAfterPx: number;
  private readonly mount?: MarketingNavigationMount;
  private previousTravel: number | null = null;
  private downwardEvidence = 0;
  private upwardEvidence = 0;
  private header: NavigationSnapshot["header"] = "visible";
  private documentScroll: number | null = null;

  constructor(options: MarketingNavigationOptions = {}) {
    this.hideAfterPx = Math.max(1, options.hideAfterPx ?? 24);
    this.showAfterPx = Math.max(1, options.showAfterPx ?? 6);
    this.mount = options.mount;
  }

  /**
   * Прокрутка документа. Шапка ведёт себя одинаково на всём сайте, а не только
   * на маршруте: раньше в первой половине она не пряталась вовсе, потому что
   * ход маршрута там ещё нулевой и признака направления не было.
   */
  observeDocumentScroll(scrollY: number): void {
    this.documentScroll = scrollY;
  }

  render(frame: MarketingFrame): NavigationSnapshot {
    const routeVisible = frame.handoff > 0.02;
    const footerReached = frame.activeSection === "footer" && frame.sections.footer.progress >= 0.98;
    const source = this.documentScroll ?? frame.travelPx;
    const delta = this.previousTravel === null ? 0 : source - this.previousTravel;
    this.previousTravel = source;
    const atPageTop = this.documentScroll !== null && this.documentScroll < 40;

    if (atPageTop) {
      this.header = "visible";
      this.downwardEvidence = 0;
      this.upwardEvidence = 0;
    } else if (footerReached) {
      this.header = "visible";
      this.downwardEvidence = 0;
      this.upwardEvidence = 0;
    } else if (delta > 0) {
      this.downwardEvidence += delta;
      this.upwardEvidence = 0;
      if (this.downwardEvidence >= this.hideAfterPx) this.header = "hidden";
    } else if (delta < 0) {
      this.upwardEvidence += -delta;
      this.downwardEvidence = 0;
      if (this.upwardEvidence >= this.showAfterPx) this.header = "visible";
    }

    const snapshot: NavigationSnapshot = {
      state: "managed",
      header: this.header,
      routeVisible,
      footerReached,
    };
    this.mount?.applyNavigationSnapshot(snapshot);
    return snapshot;
  }

  destroy(): void {
    this.mount?.destroy?.();
  }
}

export class DomMarketingNavigationMount implements MarketingNavigationMount {
  constructor(private readonly header: HTMLElement) {}

  applyNavigationSnapshot(snapshot: NavigationSnapshot): void {
    const view=this.header.ownerDocument?.defaultView;
    const cinema=view?.__BB_CINEMA__;
    /* На телефоне шапку открывает первый импульс вверх, а не сама прокрутка:
       иначе она выскакивала бы на каждом переходе между разделами. */
    const mobile=view?.__BB_MOBILE_ROUTE__?.state;
    const visible=cinema?.active?cinema.headerVisible
      :mobile?.active?mobile.headerVisible
        :snapshot.header==='visible';
    this.header.classList.toggle("marketing-nav", snapshot.routeVisible);
    this.header.classList.toggle("nav-away", !visible);
    if(this.header.inert===visible)this.header.inert=!visible;
    const state=visible?'visible':'hidden';
    if(this.header.dataset.marketingHeader!==state)this.header.dataset.marketingHeader=state;
  }

  destroy(): void {
    this.header.inert=false;
    this.header.classList.remove("marketing-nav", "nav-away");
    delete this.header.dataset.marketingHeader;
  }
}
